if (!customElements.get('shop-the-look')) {
  customElements.define(
    'shop-the-look',
    class ShopTheLook extends HTMLElement {
      connectedCallback() {
        this.number = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        this.partCount = Number(this.dataset.partCount);
        this.gift = this.dataset.gift ? Number(this.dataset.gift) : null;
        this.free = Number(this.dataset.free);

        this.items = [...this.querySelectorAll('[data-item]')].map((element) => {
          const variants = JSON.parse(element.querySelector('[data-variants]').textContent);
          return {
            element,
            title: element.dataset.title,
            variants,
            options: [...element.querySelectorAll('[data-option]')],
            include: element.querySelector('[data-include]'),
            price: element.querySelector('[data-price]'),
            was: element.querySelector('[data-was]'),
            wasValue: element.querySelector('[data-was-value]'),
          };
        });

        this.count = this.querySelector('[data-count]');
        this.total = this.querySelector('[data-total]');
        this.saving = this.querySelector('[data-saving]');
        this.perk = this.querySelector('[data-perk]');
        this.error = this.querySelector('[data-error]');
        this.button = this.querySelector('[data-add]');
        this.buttonLabel = this.querySelector('[data-cta-label]');
        this.spinner = this.button.querySelector('.loading__spinner');

        this.addEventListener('change', (event) => this.onChange(event));
        this.button.addEventListener('click', () => this.onAdd());
        this.render();
      }

      onChange(event) {
        const item = this.items.find(({ element }) => element.contains(event.target));
        if (!item) return;
        // Picking a size on a part that was left out puts it back in the look.
        if (event.target.matches('[data-option]')) {
          if (!item.include.disabled) item.include.checked = true;
          event.target.closest('.shop-the-look__option').classList.remove('is-missing');
        }
        // A part left out no longer needs a size, so its reminder goes too.
        if (event.target === item.include && !item.include.checked) {
          item.element.querySelectorAll('.is-missing').forEach((option) => option.classList.remove('is-missing'));
        }
        this.error.hidden = true;
        this.render();
      }

      // The variant the chosen values point to, or null while a choice is open.
      // Parts with a single variant resolve on their own.
      resolve(item) {
        const chosen = {};
        for (const select of item.options) {
          if (!select.value) return null;
          chosen[select.dataset.option] = select.value;
        }
        return (
          item.variants.find(
            (variant) =>
              variant.available && Object.entries(chosen).every(([index, value]) => variant.options[index] === value)
          ) || null
        );
      }

      // Until a size is chosen, a part counts at its lowest available price.
      priced(item) {
        const variant = this.resolve(item);
        if (variant) return variant;
        const available = item.variants.filter((v) => v.available);
        return available.sort((a, b) => a.price - b.price)[0] || item.variants[0];
      }

      kr(cents) {
        return `${this.number.format(cents / 100)} kr.`;
      }

      render() {
        const chosen = this.items.filter((item) => item.include.checked);
        let total = 0;
        let saving = 0;
        let missing = 0;

        this.items.forEach((item) => {
          const variant = this.resolve(item);
          const shown = this.priced(item);
          item.element.classList.toggle('is-left-out', !item.include.checked);
          if (variant) {
            item.price.textContent = this.kr(variant.price);
            const onSale = variant.compare_at_price > variant.price;
            item.was.hidden = !onSale;
            if (onSale) item.wasValue.textContent = this.kr(variant.compare_at_price);
          }
          if (!item.include.checked) return;
          total += shown.price;
          if (shown.compare_at_price > shown.price) saving += shown.compare_at_price - shown.price;
          if (!variant) missing += 1;
        });

        this.count.textContent = `${chosen.length} af ${this.partCount} dele valgt`;
        this.total.textContent = chosen.length ? this.kr(total) : '';

        this.saving.hidden = saving <= 0;
        this.saving.textContent = saving > 0 ? `Du sparer ${this.kr(saving)} i forhold til førpriserne.` : '';

        const perk = this.perkText(total, chosen.length);
        this.perk.hidden = !perk;
        this.perk.textContent = perk;

        this.missing = missing;
        let label;
        if (!chosen.length) label = 'Vælg mindst én del';
        else if (missing) label = `Vælg størrelse på ${missing} ${missing === 1 ? 'del' : 'dele'}`;
        else if (chosen.length === this.partCount) label = 'Læg hele looket i kurven';
        else label = `Læg ${chosen.length} ${chosen.length === 1 ? 'del' : 'dele'} i kurven`;
        this.buttonLabel.textContent = label;
        this.button.setAttribute('aria-disabled', String(!chosen.length || missing > 0));
      }

      perkText(total, parts) {
        if (!parts) return '';
        const gift = this.gift !== null && total >= this.gift;
        const free = total >= this.free;
        if (gift && free) return 'Looket alene giver fri fragt og et gratis smykke med i pakken.';
        if (free) return 'Looket alene giver fri fragt.';
        if (gift) return 'Looket alene giver et gratis smykke med i pakken.';
        return '';
      }

      // With a size still open the button points the visitor to it instead of
      // failing, so nothing lands in the cart in a size nobody chose.
      showMissing() {
        let first = null;
        this.items.forEach((item) => {
          if (!item.include.checked || this.resolve(item)) return;
          item.options.forEach((select) => {
            if (select.value) return;
            select.closest('.shop-the-look__option').classList.add('is-missing');
            first ??= select;
          });
        });
        first?.focus();
      }

      async onAdd() {
        if (this.button.classList.contains('loading')) return;
        const chosen = this.items.filter((item) => item.include.checked);
        if (!chosen.length) return;
        if (this.missing) {
          this.showMissing();
          return;
        }

        const cart = document.querySelector('cart-notification');
        const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
        this.error.hidden = true;
        this.button.classList.add('loading');
        this.spinner?.classList.remove('hidden');

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            ...fetchConfig('json'),
            body: JSON.stringify({
              items: chosen.map((item) => ({ id: this.resolve(item).id, quantity: 1 })),
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            this.error.textContent = data.description || data.message || 'Looket kunne ikke lægges i kurven. Prøv igen.';
            this.error.hidden = false;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shop-the-look', cartData: data });

          if (!cart) {
            window.location = routes.cart_url;
            return;
          }
          this.showNotification(cart, data);
        } catch (e) {
          this.error.textContent = 'Looket kunne ikke lægges i kurven. Prøv igen.';
          this.error.hidden = false;
        } finally {
          this.button.classList.remove('loading');
          this.spinner?.classList.add('hidden');
        }
      }

      // Dawn's notification shows one product. Here every part that went in is
      // listed, and the rest of the notification is filled as usual.
      showNotification(cart, data) {
        const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

        cart.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          const html = data.sections?.[section.id];
          if (!target || !html) return;

          if (section.id === 'cart-notification-product') {
            const doc = parse(html);
            const rows = data.items
              .map((item) => doc.querySelector(`[id="cart-notification-product-${item.key}"]`))
              .filter(Boolean)
              .map((row) => document.importNode(row, true));
            if (rows.length === 1) {
              target.innerHTML = rows[0].innerHTML;
            } else {
              const list = document.createElement('div');
              list.className = 'shop-the-look-notification';
              list.append(...rows);
              target.replaceChildren(list);
            }
            return;
          }

          const source = parse(html).querySelector(section.selector || '.shopify-section');
          if (source) target.innerHTML = source.innerHTML;
        });

        document.querySelector('sticky-header')?.reveal?.();
        cart.setActiveElement?.(this.button);
        cart.open();
      }
    }
  );
}
